import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Play, X, GripVertical, Edit, Trash2 } from 'lucide-react';
import { ActionMenu } from './ui/ActionMenu';
import { SoundEmbed } from './SoundEmbed';
import type { Sound } from '../types/database';

interface SortableSoundProps {
  sound: Sound;
  playingSound: string | null;
  setPlayingSound: (id: string | null) => void;
  isMember: boolean;
  onEdit: (sound: Sound) => void;
  onDelete: (id: string) => void;
  index: number;
}

export function SortableSound({
  sound,
  playingSound,
  setPlayingSound,
  isMember,
  onEdit,
  onDelete,
  index,
  isFirst,
  isLast
}: SortableSoundProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: sound.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`p-6 bg-gray-custom/10 hover:bg-gray-custom/20 ${isDragging ? 'bg-gray-custom/10' : ''} ${isDragging ? 'rounded-xl' : ''} ${isFirst ? 'rounded-t-xl' : ''} ${isLast ? 'rounded-b-xl' : ''}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {isMember && (
            <div 
              {...attributes} 
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-custom/20 rounded"
            >
              <GripVertical className="w-5 h-5 text-light/40" />
            </div>
          )}
          <button
            onClick={() => setPlayingSound(playingSound === sound.id ? null : sound.id)}
            className="w-8 h-8 flex items-center justify-center bg-light/10 text-light rounded-full transition-colors"
          >
            {playingSound === sound.id ? (
              <X className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
          </button>
          <h3 
            onClick={() => setPlayingSound(playingSound === sound.id ? null : sound.id)} 
            className="text-lg font-medium text-light cursor-pointer"
          >
            {sound.title}
          </h3>
        </div>
        {isMember && (
          <div className="relative z-10">
            <ActionMenu
              items={[
                {
                  label: 'Edit Sound',
                  icon: <Edit className="w-4 h-4" />,
                  onClick: () => onEdit(sound),
                },
                {
                  label: 'Delete Sound',
                  icon: <Trash2 className="w-4 h-4" />,
                  onClick: () => onDelete(sound.id),
                  className: 'text-red-500 hover:text-red-500',
                },
              ]}
            />
          </div>
        )}
      </div>
      {playingSound === sound.id && (
        <div className="mt-4">
          <SoundEmbed sound={sound} />
        </div>
      )}
    </div>
  );
}